import React, { useState, useEffect } from 'react';
import CountUp from "react-countup";
import { useNavigate } from 'react-router-dom';
import { Card, CardBody, Col } from 'reactstrap';
import axios from 'axios';
import { useAuth } from '../../../Provider/AuthProvider';

interface DateFilter {
    type?: 'day' | 'month' | 'year' | 'period';
    value?: string | number | Date;
    start?: Date;
    end?: Date;
}

interface WidgetsProps {
    dateFilter?: DateFilter;
}

const Widgets = ({ dateFilter }: WidgetsProps) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [widgetData, setWidgetData] = useState({
        totalCA: 0,
        totalUnpaid: 0,
        percentagePaid: 0,
        percentageUnpaid: 0,
        totalInvoices: 0,
        totalOrders: 0
    });

    // Fonction pour formater la date
    const formatDate = (date: string | number | Date | undefined): string => {
        if (!date) return '';
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Fonction pour construire les paramètres de filtre de date
    const buildDateFilterParams = () => {
        if (!dateFilter) return {};

        switch (dateFilter.type) {
            case 'day':
                return {
                    filter_type: 'day',
                    filter_value: formatDate(dateFilter.value)
                };
            case 'month':
                return {
                    filter_type: 'month',
                    filter_value: dateFilter.value
                };
            case 'year':
                return {
                    filter_type: 'year',
                    filter_value: dateFilter.value
                };
            case 'period':
                return {
                    filter_type: 'period',
                    date_start: formatDate(dateFilter.start),
                    date_end: formatDate(dateFilter.end)
                };
            default:
                return {};
        }
    };

    // Fonction pour fetch toutes les données
    const fetchAllData = async () => {
        if (!user?.entity) return;
        
        setLoading(true);
        
        try {
            const params: any = {
                action: 'allcount',
                entity: user.entity
            };

            const dateFilters = buildDateFilterParams();
            Object.assign(params, dateFilters);

            console.log('📤 Widgets - Paramètres:', params);

            const response: any = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                params,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            if (response.data && response.success) {
                const data = response.data;

                // Debug : SQL exécuté côté backend
                if (response.context?.debug?.sql_invoices) {
                    console.log('🔍 SQL invoices:', response.context.debug.sql_invoices);
                }

                setWidgetData({
                    totalCA: data.summary?.ca_ht_total || 0,
                    totalUnpaid: data.summary?.unpaid_invoice_amount || 0,
                    percentagePaid: data.invoices?.paid_percentage || 0,
                    percentageUnpaid: data.invoices?.unpaid_percentage || 0,
                    totalInvoices: data.invoices?.total_count || 0,
                    totalOrders: (data.customer_orders?.total_count || 0) + (data.supplier_orders?.total_count || 0)
                });
            } else {
                console.error('❌ Erreur API:', response.data?.message);
                resetData();
            }
        } catch (error) {
            console.error('❌ Erreur fetch:', error);
            resetData();
        } finally {
            setLoading(false);
        }
    };

    const resetData = () => {
        setWidgetData({
            totalCA: 0,
            totalUnpaid: 0,
            percentagePaid: 0,
            percentageUnpaid: 0,
            totalInvoices: 0,
            totalOrders: 0
        });
    };

    // Navigation
    const handleNavigateToPaid = () => {
        const state: any = {
            show_paid_status: 1,
            filter_label: "Factures payées"
        };
        
        if (dateFilter) {
            if (dateFilter.type === 'period' && dateFilter.start && dateFilter.end) {
                state.date_type = 'period';
                state.date_start = dateFilter.start;
                state.date_end = dateFilter.end;
                state.filter_label = `Factures payées du ${formatDate(dateFilter.start)} au ${formatDate(dateFilter.end)}`;
            }
        }
        
        navigate('/factures', { state });
    };

    const handleNavigateToUnpaid = () => {
        const state: any = {
            show_paid_status: 0,
            filter_label: "Factures impayées"
        };
        
        if (dateFilter) {
            if (dateFilter.type === 'period' && dateFilter.start && dateFilter.end) {
                state.date_type = 'period';
                state.date_start = dateFilter.start;
                state.date_end = dateFilter.end;
                state.filter_label = `Factures impayées du ${formatDate(dateFilter.start)} au ${formatDate(dateFilter.end)}`;
            }
        }
        
        navigate('/factures', { state });
    };

    const handleNavigateToOrders = () => navigate('/commandes');
    const handleNavigateToInvoices = () => navigate('/factures');

    // Effet pour recharger quand le filtre change
    useEffect(() => {
        fetchAllData();
    }, [user?.entity, JSON.stringify(dateFilter)]);

    // Configuration des widgets
    const ecomWidgets = [
        {
            id: 1,
            label: "CA Total (HT)",
            badgeClass: widgetData.percentagePaid >= 0 ? "success" : "danger",
            badge: widgetData.percentagePaid >= 0 ? "ri-arrow-up-line" : "ri-arrow-down-line",
            percentage: widgetData.percentagePaid.toFixed(1),
            prefix: "",
            suffix: "€",
            separator: ",",
            counter: widgetData.totalCA,
            decimals: 2,
            link: "Voir détails",
            onClick: handleNavigateToPaid, 
            bgcolor: "success",
            icon: "ri-money-dollar-circle-line",
            loading: loading
        },
        {
            id: 2,
            label: "Commandes",
            badgeClass: "success",
            badge: widgetData.totalOrders > 0 ? "ri-arrow-up-line" : "ri-arrow-down-line",
            percentage: widgetData.totalOrders > 0 ? "100" : "0",
            prefix: "",
            suffix: "",
            separator: "",
            counter: widgetData.totalOrders,
            decimals: 0,
            link: "Voir commandes",
            onClick: handleNavigateToOrders,
            bgcolor: "info",
            icon: "ri-shopping-cart-2-line",
            loading: loading
        },
        {
            id: 3,
            label: "Factures",
            badgeClass: "success",
            badge: widgetData.totalInvoices > 0 ? "ri-arrow-up-line" : "ri-arrow-down-line",
            percentage: widgetData.totalInvoices > 0 ? "100" : "0",
            prefix: "",
            suffix: "",
            separator: "",
            counter: widgetData.totalInvoices,
            decimals: 0,
            link: "Voir factures",
            onClick: handleNavigateToInvoices,
            bgcolor: "warning",
            icon: "ri-file-text-line",
            loading: loading
        },
        {
            id: 4,
            label: "Impayés",
            badgeClass: widgetData.percentageUnpaid > 5 ? "danger" : "warning",
            badge: widgetData.percentageUnpaid > 5 ? "ri-arrow-up-line" : "ri-arrow-down-line",
            percentage: widgetData.percentageUnpaid.toFixed(1),
            prefix: "",
            suffix: "€",
            separator: ",",
            counter: widgetData.totalUnpaid,
            decimals: 2,
            link: "Voir impayés",
            onClick: handleNavigateToUnpaid,
            bgcolor: "danger",
            icon: "ri-alert-line",
            loading: loading
        }
    ];

    return (
        <React.Fragment>
            {ecomWidgets.map((item, key) => (
                <Col xl={3} md={6} key={key}>
                    <Card className="card-animate">
                        <CardBody>
                            <div className="d-flex align-items-center">
                                <div className="flex-grow-1 overflow-hidden">
                                    <p className="text-uppercase fw-medium text-muted text-truncate mb-0">
                                        {item.label}
                                        {item.loading && (
                                            <span className="ms-2">
                                                <i className="ri-loader-4-line ri-spin"></i>
                                            </span>
                                        )}
                                    </p>
                                </div>
                                <div className="flex-shrink-0">
                                    <h5 className={"fs-14 mb-0 text-" + item.badgeClass}>
                                        {item.badge ? <i className={"fs-13 align-middle " + item.badge}></i> : null} {item.percentage} %
                                    </h5>
                                </div>
                            </div>
                            <div className="d-flex align-items-end justify-content-between mt-4">
                                <div>
                                    <h4 className="fs-20 fw-semibold ff-secondary mb-4">
                                        <span className="counter-value">
                                            {item.loading ? (
                                                <span className="text-muted"></span>
                                            ) : (
                                                <CountUp
                                                    start={0}
                                                    prefix={item.prefix}
                                                    suffix={item.suffix}
                                                    separator={item.separator}
                                                    end={item.counter}
                                                    decimals={item.decimals}
                                                    duration={2}
                                                />
                                            )}
                                        </span>
                                    </h4>
                                    
                                    <button 
                                        onClick={item.onClick} 
                                        className="text-decoration-underline btn btn-link p-0"
                                        style={{ border: 'none', background: 'none', color: '#0d6efd', cursor: 'pointer' }}
                                    >
                                        {item.link}
                                    </button>
                                </div>
                                <div className="avatar-sm flex-shrink-0">
                                    <span className={"avatar-title rounded fs-3 bg-" + item.bgcolor + "-subtle"}>
                                        <i className={`text-${item.bgcolor} ${item.icon}`}></i>
                                    </span>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            ))}
        </React.Fragment>
    );
};

export default Widgets;